/* eslint-disable no-shadow */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {usePlausible} from 'next-plausible';
import {useRouter} from 'next/router';
import {
  BigNumber,
  formatEther,
  FormattableCurrency,
  // parseUnits,
  toNativeCurrencyString,
  TransactionReceipt,
  TransactionResponse,
} from '@ttx/core';
import {
  EthereumNetworkIcon,
  PolygonIcon,
  Typography,
  ZilliqaIcon,
  // BitcoinIcon,
  TokenTraxxIcon,
} from '@ttx/design-system';
// import {CircleCheckoutError} from '@circle-fin/circle-widgets-sdk/lib/interfaces/CircleCheckoutError';
// import {PaymentSuccessResult} from '@circle-fin/circle-widgets-sdk/lib/interfaces/PaymentSuccessResult';
import {keccak256} from '@ethersproject/keccak256';
import {MerkleTree} from 'merkletreejs';
import {ethers} from 'ethers';
import {useWallets} from '@privy-io/react-auth';
import {useToastContext} from '../contexts/toast-context';
import {useRightSideDrawer} from '../components/right-side-drawer';
import {useAuthenticationContext} from '../contexts/authentication-context';
import {useSmartContractContext} from '../contexts/smart-contract/context';
import {WhyConnectWalletModal} from '../components/purchase-or-download-cta/purchase-cta/why-connect-wallet-modal';
import {
  CashPaymentDetailsModal,
  PaymentDetailsFormProps,
} from '../components/purchase-or-download-cta/purchase-cta/cash-payment-details-modal/cash-payment-details-modal';
import {CashProcessingModal} from '../components/purchase-or-download-cta/purchase-cta/processing-modals/cash-processing-modal';
import {LiFiCashOrCrypo} from '../components/purchase-or-download-cta/cta-modals/lifi-cash-or-crypto';
import {CashPendingModal} from '../components/purchase-or-download-cta/purchase-cta/processing-modals/cash-pending-modal';
import {CashFailedModal} from '../components/purchase-or-download-cta/purchase-cta/processing-modals/cash-failed-modal';
import {CryptoProcessingModal} from '../components/purchase-or-download-cta/purchase-cta/processing-modals/crypto-processing-modal';
import {Network, WalletNetwork} from '../contexts/authentication-context/types';
import {CashValues, CryptoValues, ModalVariant} from '../utils/types';
import {useTtxMutation, useTtxQuery} from './ttx-trpc';
import {Transaction} from '../server/router/user/types';
import {PaymentMethodModal} from '../components/purchase-or-download-cta/cta-modals/payment-method-modal';
import {CompleteCheckoutModal} from '../components/purchase-or-download-cta/cta-modals/complete-checkout-modal';
// import {MissingTokensModal} from '../components/purchase-or-download-cta/cta-modals/missing-tokens-modal';
import {SignUpInModal} from '../components/purchase-or-download-cta/cta-modals/signup-in-modal';
import {WrongNetworkModal} from '../components/purchase-or-download-cta/cta-modals/wrong-network-modal';
import {SuccessModal} from '../components/purchase-or-download-cta/cta-modals/success-modal';
import {GuestUserModal} from '../components/purchase-or-download-cta/cta-modals/guest-users-modal';
import {TokenOwnerModal} from '../components/purchase-or-download-cta/cta-modals/token-owner-modal';
import {MaxPurchaseModal} from '../components/purchase-or-download-cta/cta-modals/max-purchase-warning-modal';
import {mixpanelTrack} from '../contexts/analytics-context/utils';
import {
  LocalStroageUtil,
  LOCAL_STORAGE_KEY,
} from '../utils/local-storage-utils';
import {
  SARA_DROP,
  SARA_NFT,
  // traxxStemzSlug,
  // emilGSlug,
  VENLY_NETWORK,
  // withPaperContractInfo,
  JOSH_WHITELISTED_USERS,
  // joshSavageSlug,
} from '../utils/consts';
import {PaymentType} from '../components/wallet-connection-button-group';
import {DynamicConfirmationModal} from '../components/purchase-or-download-cta/cta-modals/dynamic-confirmation-modal';
import {hasSessionItem, setSessionItem} from '../utils/session-storage-util';
import {checkAddressExistsInWhiteList} from '../pages/tracks/utils';
import {DynamicContentModal} from '../components/purchase-or-download-cta/cta-modals/dynamic-content-modal';
import {MultiDropModal} from '../components/purchase-or-download-cta/cta-modals/multidrop-modal';
import {FIATPaymentPaused} from '../components/purchase-or-download-cta/cta-modals/fiat-payment-paused';
import {SquareupCheckoutModal} from '../components/purchase-or-download-cta/cta-modals/squareup/checkout-squareup-modal';
import {useLifiWidget} from '../contexts/lifi-context';

interface Event {
  event: string;
  args: {
    tokenId: BigNumber;
  };
}

interface TxnReceipt extends TransactionReceipt {
  events?: Event[];
}

const SQUAREUP_MIN_PAYMENT = 0.5;

const setTrackNetwork = (network: string) => {
  const existNetwork = LocalStroageUtil.getItem(
    LOCAL_STORAGE_KEY.TRACK_NETWORK,
  );
  if (existNetwork === network) return;
  LocalStroageUtil.setItem(LOCAL_STORAGE_KEY.TRACK_NETWORK, network);
};

// const CONFIRM_TITLE = 'Do you own a Data Vaeda Pass ?';
const PICK_NFT_TITLE =
  'GM cosmic traveller. Welcome to planet Vaedar. Choose your collectible:';

// function addMinutes(date: any, minutes: number) {
//   date.setMinutes(date.getMinutes() + minutes);
//   return date;
// }

interface IGasEstimate {
  gasEstimate: string | null;
}

export const useCheckoutModals: (props: {
  currencySymbol: FormattableCurrency | null;
  network?: WalletNetwork;
  modalVariant: ModalVariant | null;
  setModalVariant: (modalType: ModalVariant | null) => void;
  canUseCash: boolean;
  props: {
    onStartCryptoCheckout: () => Promise<TransactionResponse>;
    onEstimateGasFee?: () => Promise<IGasEstimate>;
    onStartCashCheckout: (values: PaymentDetailsFormProps) => Promise<void>;
    onIncrement: () => void;
    onDecrement: () => void;
    onResetQuantity: (qty: number) => void;
    quantity: number;
    trackName?: string;
    artist?: string;
    userEmail?: string;
    paymentStatus?: string;
    imageSrc?: string;
    isMultiCopy?: boolean;
    isTraxxStemz?: boolean;
    isJoshSavage?: boolean;
    isEmilG?: boolean;
    crypto: CryptoValues;
    cash: CashValues;
    loading: boolean;
    primarySaleUserCount?: number;
    isAllowedUser?: boolean;
    maxPurchaseLimit?: number;
    buyoutPrice: string;
    nftId: string;
    listingId: string;
    url: string;
    slug: string;
    country: string;
    whiteList?: string[];
    currency?: string;
    proof?: string[];
    tokenId?: number;
  };
  tokenOwner?: string;
  isMember?: boolean;
}) => {
  modalVariantComponent: JSX.Element | null;
  onPressPurchase: () => void;
} = ({
  currencySymbol,
  network,
  modalVariant,
  setModalVariant,
  canUseCash,
  props,
  tokenOwner,
  isMember,
}) => {
  const {wallets} = useWallets();
  const privyWallet = wallets.find(
    (wallet) => wallet.walletClientType === 'privy',
  );
  const toast = useToastContext();
  const plausible = usePlausible();
  const {openWallet} = useRightSideDrawer();
  const router = useRouter();

  const NETWORK = VENLY_NETWORK[network as keyof typeof VENLY_NETWORK];
  const {paymentId} = router.query;

  const {
    walletConnected,
    isAuthenticated,
    walletNetwork,
    walletNativeCurrencyBalance,
    walletProvider,
    connectWallet,
    walletAddress,
    _web3React,
  } = useAuthenticationContext();

  const {isExchangeCompleted, handleWidgetModal} = useLifiWidget();
  const {supportedPaymentTokenBalances} = useSmartContractContext();

  const {data: networksData} = useTtxQuery(['traxx.getNetworks']);
  const [purchaseTokenIds, setPurchaseTokenIds] = useState<number[]>([]);
  const [isCryptoFlowSelected, setIsCryptoFlowSelected] = useState<
    boolean | null
  >(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [clientKey, setClientKey] = useState<string>('');
  const [gasFee, setGasFee] = useState<string>('');
  const [totalFee, setTotalFee] = useState<string | null>(props.cash.total);
  const [isWidgetVisible, setIsWidgetVisible] = useState<boolean>(false);
  const [privyNetwork, setPrivyNetwork] = useState<string>('polygon');
  // const [withPaperSignature, setWithPaperSignature] = useState<string>('');
  const [discountSignature, setDiscountSignature] = useState<string>('0x');
  const [discountTimeStamp, setDiscountTimeStamp] = useState<number>(
    Date.now(),
  );
  // const [expireTimeStamp, setExpireTimeStamp] = useState<number>(
  //   Math.floor(addMinutes(new Date(), 5) / 1000),
  // );
  const [widgetLoading, setWidgetLoading] = useState(false);

  const {data} = useTtxQuery(
    [
      'user.get-transaction',
      {
        limit: 0,
        page: 1,
        paymentId: paymentId as string,
      },
    ],
    {
      refetchInterval: paymentId ? 10000 : false,
      enabled: !!isAuthenticated(),
    },
  );

  // const {isLoading, error, ...checkoutMutation} = useTtxMutation(
  //   'auth.circle-checkout-session',
  // );

  const {...addRegion} = useTtxMutation('nft.add-region');

  const {mutateAsync: logoutMutation} = useTtxMutation('auth.logout');

  const closeModal = useCallback(() => {
    setIsWidgetVisible(false);
    setWidgetLoading(false);
    setSessionId('');
    setClientKey('');
    setModalVariant(null);
    if (props.onResetQuantity) {
      let qty = 1;
      if (props.isTraxxStemz) {
        // && props.maxPurchaseLimit
        // qty = props.maxPurchaseLimit - (props.primarySaleUserCount || 0);
        qty = 5;
      }
      props.onResetQuantity(qty);
    }
    if (paymentId) {
      LocalStroageUtil.setItem(LOCAL_STORAGE_KEY.PAYMENT_ID, paymentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setModalVariant, props.maxPurchaseLimit, props.primarySaleUserCount]);

  // const nativeCurrency = network === 'ethereum' ? 'ETH' : 'MATIC';

  // const {data: nativePriceData} = useTtxQuery(
  //   [
  //     'traxx.price',
  //     {
  //       currencies: ['USD'],
  //       primaryCurrency: nativeCurrency,
  //       isProd: true,
  //     },
  //   ],
  //   {
  //     refetchInterval: false,
  //   },
  // );

  // const {data: traxxPriceData} = useTtxQuery(
  //   [
  //     'traxx.price',
  //     {
  //       currencies: ['USD'],
  //       primaryCurrency: 'TRAXX',
  //       isProd: true,
  //     },
  //   ],
  //   {
  //     refetchInterval: false,
  //   },
  // );

  // let withPaperPrice = '0';
  // if (
  //   nativePriceData?.length &&
  //   traxxPriceData?.length &&
  //   nativePriceData[0]?.price &&
  //   traxxPriceData[0]?.price &&
  //   props.buyoutPrice
  // ) {
  //   withPaperPrice = (
  //     (traxxPriceData[0].price / nativePriceData[0].price) *
  //     Number(
  //       toNativeCurrencyString(
  //         toNativeCurrencyString(props.buyoutPrice, 'ETH'),
  //         'ETH',
  //       ),
  //     )
  //   ).toFixed(18);
  // }

  const [processingTransactionHash, setProcessingTransactionHash] = useState<
    string | undefined
  >();

  const networkDetails = useMemo(
    () => networksData?.filter((item) => item.network === network).shift(),
    [networksData, network],
  );

  const handleChangeNetwork = useCallback(async () => {
    // eslint-disable-next-line no-extra-boolean-cast
    if (!!privyWallet) {
      await privyWallet.switchChain(networkDetails?.chainId);
      setPrivyNetwork(network || 'polygon');
      setModalVariant(null);
    } else if (walletProvider === 'venly') {
      _web3React.deactivate();
      await logoutMutation();
      try {
        await connectWallet(
          'venly',
          network === 'polygon' ? 'MATIC' : 'ETHEREUM',
          walletNetwork !== network,
        );
        setModalVariant(null);
      } catch (e) {
        toast.addErrorToast('Something went wrong. Please try again later.');
      }
    } else if (walletProvider === 'paper') {
      _web3React.deactivate();
      await logoutMutation();
      try {
        await connectWallet(
          'paper',
          network === 'polygon' ? 'MATIC' : 'ETHEREUM',
          walletNetwork !== network,
        );
        setModalVariant(null);
      } catch (e) {
        toast.addErrorToast('Something went wrong. Please try again later.');
      }
    } else if (network === 'zilliqa') {
      await connectWallet('zilPay');
      setModalVariant(null);
    } else if (walletNetwork === 'zilliqa') {
      await connectWallet('metaMask', network as Network);
      setModalVariant(null);
    } else {
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{chainId: `0x${networkDetails?.chainId.toString(16)}`}],
        });
        setModalVariant(null);
      } catch (switchError: any) {
        // This error code indicates that the chain has not been added to MetaMask.
        if (switchError.code === 4902) {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: `0x${networkDetails?.chainId.toString(16)}`,
                  chainName: networkDetails?.displayName,
                  rpcUrls: [networkDetails?.rpcUrl],
                  nativeCurrency: {
                    name: network === 'polygon' ? 'Matic' : 'Eth',
                    symbol: network === 'polygon' ? 'MATIC' : 'ETH',
                    decimals: 18,
                  },
                },
              ],
            });
            setModalVariant(null);
          } catch (addError) {
            toast.addErrorToast(
              'Something went wrong. Please try again later.',
            );
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    walletProvider,
    networkDetails?.chainId,
    networkDetails?.displayName,
    networkDetails?.rpcUrl,
    setModalVariant,
    network,
    toast,
    walletNetwork,
  ]);

  const {...stemzAddNFT} = useTtxMutation('traxxstemz.add-stemz-nft');
  const {...joshAddNFT} = useTtxMutation('traxxstemz.add-josh-nft');
  const {...emilgAddNFT} = useTtxMutation('emilg.add-emilg-nft');
  // const {...withPaperSign} = useTtxMutation('traxx.withpaper-sign');
  // const {...adminSignMessage} = useTtxMutation('evm.admin-sign-message');
  // Discount for selected drops | users endpoint
  // const {...discountSignMessage} = useTtxMutation('evm.drop-discount');

  const handleBuyWithCrypto = useCallback(async () => {
    const {artist, trackName, onStartCryptoCheckout} = props;
    try {
      setModalVariant('crypto-processing');

      const purchaseTransaction = await onStartCryptoCheckout();
      if (!purchaseTransaction) {
        throw new Error('Your transaction got failed!');
      }
      setProcessingTransactionHash(purchaseTransaction.hash);
      const txn: TxnReceipt = await purchaseTransaction.wait();
      await addRegion.mutateAsync({region: props.country});
      if (props.isTraxxStemz) {
        const tokenIds =
          txn.events
            ?.filter(({event}) => event === 'Transfer')
            .map(({args: {tokenId}}) => Number(tokenId)) || [];
        setPurchaseTokenIds(tokenIds);
        await stemzAddNFT.mutateAsync({
          tokenId: tokenIds,
        });
      } else if (props.isJoshSavage) {
        const tokenIds =
          txn.events
            ?.filter(({event}) => event === 'Transfer')
            .map(({args: {tokenId}}) => Number(tokenId)) || [];
        setPurchaseTokenIds(tokenIds);
        await joshAddNFT.mutateAsync({
          tokenId: tokenIds,
        });
      } else if (props.isEmilG) {
        const tokenIds =
          txn.events
            ?.filter(({event}) => event === 'Transfer')
            .map(({args: {tokenId}}) => Number(tokenId)) || [];
        setPurchaseTokenIds(tokenIds);
        await emilgAddNFT.mutateAsync({
          tokenId: tokenIds,
        });
      }
      plausible('Checkout successful - Crypto Flow');
      mixpanelTrack('Checkout successful - Crypto Flow');
      // setModalVariant(null);
      // toast.addPaymentSuccessToast(
      //   artist || '',
      //   trackName || '',
      //   purchaseTransaction.hash,
      // );
      // plausible('Checkout successful');
      setModalVariant('success');
    } catch (e: unknown) {
      setModalVariant(null);
      let errResponse = (e as any)?.data?.message || (e as any)?.message || '';
      if (
        ((props.isTraxxStemz || props.isEmilG || props.isJoshSavage) &&
          errResponse.includes('execution reverted: 68')) ||
        errResponse.includes('execution reverted: 50') ||
        errResponse.includes('execution reverted: 63')
      ) {
        errResponse = 'Exceeded individual token limit!';
      }
      let qty = 1;
      if (props.isTraxxStemz) {
        // && props.maxPurchaseLimit
        // qty = props.maxPurchaseLimit - (props.primarySaleUserCount || 0);
        qty = 5;
      }
      props.onResetQuantity(qty);
      toast.addPaymentErrorToast(
        artist || '',
        trackName || '',
        processingTransactionHash,
        errResponse,
      );
      if (errResponse.includes('denied transaction')) {
        plausible('Metamask Signature Rejected During Purchase');
      }
      plausible('Checkout unsuccessful');
      mixpanelTrack('Checkout unsuccessful');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plausible, processingTransactionHash, props, toast]);

  const paymentTokenBalance = supportedPaymentTokenBalances.find(
    ({symbol}) => symbol === currencySymbol,
  );

  const insufficientNativeCurrency =
    Number(formatEther(walletNativeCurrencyBalance || '0')) <
    Number(props.crypto.total || 0);

  const insufficientTraxx =
    Number(paymentTokenBalance?.balance) < Number(props.crypto.total) &&
    currencySymbol === 'TRAXX';

  const navigateToCryptoCheckout = useCallback(() => {
    if (network === 'zilliqa' || network === ('bitcoin' as WalletNetwork))
      return;
    const hasFunds =
      !paymentTokenBalance ||
      (paymentTokenBalance?.balance && Number(paymentTokenBalance.balance) > 0);
    const pickNFT = hasSessionItem('PICKED_NFT');
    const isSaraDrop = SARA_NFT.includes(props.slug);
    if (walletNetwork !== network && privyNetwork !== network) {
      plausible('Purchase attempted on wrong network');
      setModalVariant('wrong-network');
    } else if (!pickNFT && isSaraDrop) {
      setModalVariant('pick-nft');
    } else if (
      !hasFunds ||
      (!paymentTokenBalance && insufficientNativeCurrency) ||
      insufficientTraxx
    ) {
      setModalVariant('missing-tokens');
    } else if (
      (props.isTraxxStemz || props.isEmilG) &&
      props?.primarySaleUserCount &&
      props?.maxPurchaseLimit &&
      props?.primarySaleUserCount >= props?.maxPurchaseLimit &&
      !props.isJoshSavage
    ) {
      setModalVariant('max-purchase');
    } else if (!props.isAllowedUser) {
      setModalVariant('missing-guest');
    } else {
      setModalVariant('checkout');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props,
    props.isAllowedUser,
    supportedPaymentTokenBalances,
    walletNetwork,
    insufficientNativeCurrency,
    network,
  ]);

  const navigateCheckout = () => {
    if (network === 'zilliqa' || network === ('bitcoin' as WalletNetwork))
      return;
    const hasFunds =
      !paymentTokenBalance ||
      (paymentTokenBalance?.balance && Number(paymentTokenBalance.balance) > 0);

    const pickNFT = hasSessionItem('PICKED_NFT');
    const isSaraDrop = SARA_NFT.includes(props.slug);
    if (walletNetwork !== network && privyNetwork !== network) {
      plausible('Purchase attempted on wrong network');
      setModalVariant('wrong-network');
    } else if (!pickNFT && isSaraDrop) {
      setModalVariant('pick-nft');
    } else if (
      !hasFunds ||
      (!paymentTokenBalance && insufficientNativeCurrency) ||
      insufficientTraxx
    ) {
      setModalVariant('missing-tokens');
    } else if (
      (props.isTraxxStemz || props.isEmilG) &&
      props?.primarySaleUserCount &&
      props?.maxPurchaseLimit &&
      props?.primarySaleUserCount >= props?.maxPurchaseLimit &&
      !props.isJoshSavage
    ) {
      setModalVariant('max-purchase');
    } else if (!props.isAllowedUser) {
      setModalVariant('missing-guest');
    } else {
      setModalVariant('checkout');
    }
  };

  const navigateToCashCheckout = useCallback(() => {
    if (network === 'zilliqa' || network === ('bitcoin' as WalletNetwork))
      return;
    if (walletNetwork !== network && privyNetwork !== network) {
      plausible('Purchase attempted on wrong network');
      setModalVariant('wrong-network');
    } else {
      const pickNFT = hasSessionItem('PICKED_NFT');
      const isSaraDrop = SARA_NFT.includes(props.slug);
      if (!pickNFT && isSaraDrop) {
        setModalVariant('pick-nft');
      } else {
        // setModalVariant('cash-payment-details');
        setModalVariant('cash-nft-checkout');
        // setModalVariant('fiat-paused');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletNetwork, network]);

  useEffect(() => {
    if (walletConnected && modalVariant === 'signupin-crypto') {
      if (walletProvider === 'paper') {
        setModalVariant('awaiting-signature-cash');
      } else {
        setModalVariant('awaiting-signature-crypto');
      }
    }
    if (walletConnected && modalVariant === 'signupin-cash') {
      if (props.isAllowedUser) {
        setModalVariant('awaiting-signature-cash');
      } else {
        setModalVariant('missing-guest');
      }
    }
    // Only update state when the wallet connection status changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletConnected, walletProvider, props.isAllowedUser]);

  const authenticated = isAuthenticated();

  useEffect(() => {
    if (
      !isExchangeCompleted ||
      walletProvider === 'venly' ||
      walletProvider === 'paper'
    )
      return;
    setModalVariant(null);
    navigateCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExchangeCompleted, supportedPaymentTokenBalances]);

  useEffect(() => {
    if (authenticated && modalVariant === 'awaiting-signature-crypto') {
      if (walletProvider === 'paper') {
        navigateToCashCheckout();
      } else {
        navigateToCryptoCheckout();
      }
    }
    if (authenticated && modalVariant === 'awaiting-signature-cash') {
      if (props.isAllowedUser) {
        navigateToCashCheckout();
      } else {
        setModalVariant('missing-guest');
      }
    }
    // If the user is in the checkout flow, once the user has signed, start purchase flow.
    // Only update when the authentication status changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, props.isAllowedUser]);

  useEffect(() => {
    // if (props.paymentStatus === 'confirmed') {
    //   const savedPaymentId = LocalStroageUtil.getItem(
    //     LOCAL_STORAGE_KEY.PAYMENT_ID,
    //   );
    //   if (!savedPaymentId || savedPaymentId !== paymentId) {
    //     setModalVariant('cash-processing');
    //   }
    // }
    if ((data as Transaction)?.status === 'pending') {
      setModalVariant('cash-pending');
      LocalStroageUtil.removeItem(LOCAL_STORAGE_KEY.PAYMENT_ID);
    }
    if ((data as Transaction)?.status?.includes('failed')) {
      setModalVariant('cash-failed');
      LocalStroageUtil.removeItem(LOCAL_STORAGE_KEY.PAYMENT_ID);
    }
    if (
      (data as Transaction)?.status === 'passedOnBlockchain' &&
      props.tokenId
    ) {
      // const tokenIds = (data as Transaction).nft?.map((el) => el.tokenId);
      plausible('Checkout successful - Fiat Flow');
      mixpanelTrack('Checkout successful - Fiat Flow');
      setPurchaseTokenIds([props.tokenId]);
      setModalVariant('success');
      LocalStroageUtil.removeItem(LOCAL_STORAGE_KEY.PAYMENT_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.paymentStatus, setModalVariant, data, props.tokenId]);

  const handleSelectCashPayment = useCallback(() => {
    if (!walletConnected) {
      setTrackNetwork(NETWORK);
      setModalVariant('signupin-cash');
    } else {
      setIsCryptoFlowSelected(false);
      // setModalVariant('fiat-paused');
      navigateToCashCheckout();
      plausible('Clicked pay with cash');
      mixpanelTrack('Clicked pay with cash');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateToCashCheckout, walletConnected]);

  const handleSelectCryptoPayment = useCallback(() => {
    if (!walletConnected) {
      setTrackNetwork(NETWORK);
      setModalVariant('signupin-crypto');
    } else {
      setIsCryptoFlowSelected(true);
      navigateToCryptoCheckout();
      plausible('Clicked pay with crypto');
      mixpanelTrack('Clicked pay with crypto');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateToCryptoCheckout, walletConnected]);

  const {openPurchaseHistory} = useRightSideDrawer();

  const onViewTransactions = useCallback(() => {
    openPurchaseHistory();
    closeModal();
  }, [openPurchaseHistory, closeModal]);

  const onPressPurchaseButton = useCallback(() => {
    plausible('Purchase clicked');
    mixpanelTrack('Purchase clicked');
    // Only allow cash purchase flow if NFT is not free
    // and has a price that is at least $0.5
    if (!props.crypto.price && !props.cash.price && !props.isJoshSavage) return;
    const pickNFT = hasSessionItem('PICKED_NFT');
    const isSaraDrop = SARA_NFT.includes(props.slug);
    if (!pickNFT && isSaraDrop && walletAddress) {
      setModalVariant('pick-nft');
    } else if (!props.isAllowedUser && walletAddress) {
      setModalVariant('missing-guest');
    } else if (
      Number(props.cash.total?.replace('$', '')) >= SQUAREUP_MIN_PAYMENT &&
      !props.isTraxxStemz &&
      !props.isEmilG &&
      !props.isJoshSavage
    ) {
      setModalVariant('cash-or-crypto');
    } else if (
      walletAddress &&
      tokenOwner &&
      walletAddress.toString() === tokenOwner?.toString()
    ) {
      setModalVariant('token-owner');
    } else if (
      !props.isJoshSavage &&
      (!Number(props.crypto.price) ||
        Number(props.cash.total?.replace('$', '')) < SQUAREUP_MIN_PAYMENT ||
        // Can only use cash if balance of crypto exists in treasury account
        !canUseCash)
    ) {
      handleSelectCryptoPayment();
    } else {
      const maxPurchaseLimit = props?.maxPurchaseLimit || 0;
      const primarySaleUserCount = props.primarySaleUserCount || 0;
      if (
        walletAddress &&
        ((maxPurchaseLimit !== 0 &&
          maxPurchaseLimit < props.quantity + primarySaleUserCount &&
          !props.isJoshSavage) ||
          (props.isJoshSavage && maxPurchaseLimit < props.quantity))
      ) {
        setModalVariant('max-purchase');
      } else if (props.isAllowedUser || !walletAddress) {
        setModalVariant('cash-or-crypto');
      } else {
        setModalVariant('missing-guest');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleSelectCryptoPayment,
    plausible,
    props.cash.total,
    props.cash.price,
    props.crypto.price,
    setModalVariant,
    canUseCash,
    props.isAllowedUser,
    walletAddress,
  ]);

  const onModalClose = useCallback(() => {
    closeModal();
    let redirectRoute = 'tracks';
    if (props.isTraxxStemz) {
      redirectRoute = 'traxxstemz';
    } else if (props.isJoshSavage) {
      redirectRoute = 'joshSavage';
    } else if (props.isEmilG) {
      redirectRoute = 'emilg';
    }
    router.replace(`/${redirectRoute}/${props.slug}`, undefined, {
      shallow: true,
    });
  }, [closeModal, router, props]);

  const onSuccessOkPress = (dropId: string | null = null) => {
    if (isCryptoFlowSelected !== null && isCryptoFlowSelected) {
      LocalStroageUtil.setItem(LOCAL_STORAGE_KEY.IS_CRYPTO_FLOW, true);
    }
    if (props.isTraxxStemz || props.isEmilG || props.isJoshSavage) {
      const redirectUrl = dropId ? `/my-nfts/${dropId}` : `/my-nfts`;
      router.push(redirectUrl);
    } else {
      router.push(`/my-nfts/${props.nftId}`);
    }
  };
  let dropType = 'default';
  if (props.isTraxxStemz) {
    dropType = 'g6ts';
  }
  if (props.isJoshSavage) {
    dropType = 'js';
  }
  if (props.isEmilG) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dropType = 'emilg';
  }

  useEffect(() => {
    if (
      !isAuthenticated() ||
      !walletConnected ||
      !walletNetwork ||
      !props.onEstimateGasFee ||
      !props.cash.total
    )
      return;
    props
      .onEstimateGasFee()
      .then((response) => {
        console.log('response', response);
        const gasValue =
          String(Number(response?.gasEstimate?.replace('$', ''))) || '0.00';
        setGasFee(gasValue + Number(props.cash.squareupTransactionFee));
        const totalValue = String(Number(props.cash.total) + Number(gasValue));
        console.log('totalValue', totalValue);
        setTotalFee(totalValue);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error: ', err.message);
        setGasFee('0.00');
        setTotalFee(props.cash.total as string);
      });
    // setGasFee('$0.00');
    // setTotalFee(props.cash.total as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, walletConnected, walletNetwork, props.cash.total]);

  // const handleConfirmPurchase = async () => {
  //   if (!walletConnected || props.isTraxxStemz || props.isEmilG) return;
  //   plausible('Fiat Purchase Confirmed');
  //   setWidgetLoading(true);
  //   try {
  //     let signature = '';

  //     if (currencySymbol === 'TRAXX') {
  //       const date = new Date();
  //       const newDate = addMinutes(date, 10);
  //       const timeStamp = Math.floor(newDate / 1000);
  //       setExpireTimeStamp(timeStamp);
  //       signature = (await withPaperSign.mutateAsync({
  //         buyer: walletAddress as string,
  //         price: `${BigNumber.from(parseUnits(withPaperPrice, 18))
  //           .mul(BigNumber.from(props.quantity))
  //           .toString()}`,
  //         network: network as string,
  //         tokenId: props.tokenId?.toString() as string,
  //         nft: props.nftId,
  //         quantity: props.quantity,
  //         timestamp: timeStamp,
  //       })) as string;
  //     }

  //     let sigHash = '0x';
  //     const currentStamp = Date.now();
  //     const isMembershipNFT =
  //       props.slug?.includes('membership') &&
  //       props.slug !== 'my-sahel-membership-nft';
  //     if (isMember && !isMembershipNFT) {
  //       sigHash = (await adminSignMessage.mutateAsync({
  //         timeStamp: currentStamp,
  //       })) as unknown as string;
  //     }
  //     // TODO: Rewrite in more generic way
  //     if (props.slug === 'life-without-water') {
  //       try {
  //         sigHash = (await discountSignMessage.mutateAsync({
  //           timeStamp: currentStamp,
  //         })) as unknown as string;
  //       } catch (e) {
  //         sigHash = '0x';
  //       }
  //     }
  //     if (signature || currencySymbol !== 'TRAXX') {
  //       setWidgetLoading(false);
  //       setWithPaperSignature(signature as string);
  //       setIsWidgetVisible(true);
  //       setDiscountSignature(sigHash);
  //       setDiscountTimeStamp(currentStamp);
  //     }
  //   } catch (e) {
  //     toast.addErrorToast('Something went wrong. Please try again later.');
  //     setWidgetLoading(false);
  //     setModalVariant(null);
  //   }

  //   // if (!walletConnected) return;
  //   // plausible('Fiat Purchase Confirmed');
  //   // const data = await checkoutMutation.mutateAsync({
  //   //   successUrl: `${ENVIRONMENT === 'local' ? 'http://' : 'https://'}${
  //   //     props.url
  //   //   }`,
  //   //   amount: Number(totalFee?.replace('$', '')),
  //   //   fiatCurrency: 'USD',
  //   //   nftId: props.nftId,
  //   //   listingId: props.listingId,
  //   //   quantity: props.quantity,
  //   //   nftPrice: props.buyoutPrice,
  //   //   dropType,
  //   //   skipSave: false,
  //   // });
  //   // plausible('Checkout Widget Opened');
  //   // const {sessionId: sessionResponse, clientToken} = data;
  //   // setSessionId(sessionResponse);
  //   // setClientKey(clientToken);
  // };

  // const handleWidgetError = (error: PaperSDKError) => {
  //   if (error) setModalVariant('cash-failed');
  // };

  // const handleWidgetSuccess = async (response: SuccessProps) => {
  //   setModalVariant('cash-pending');
  //   await checkoutMutation.mutateAsync({
  //     amount: props.isJoshSavage ? 0 : Number(totalFee?.replace('$', '')),
  //     fiatCurrency: 'USD',
  //     nftId: props.nftId,
  //     listingId: props.listingId,
  //     quantity: props.quantity,
  //     nftPrice: props.buyoutPrice,
  //     dropType,
  //     skipSave: false,
  //     transactionId: response.transactionId,
  //   });
  //   await addRegion.mutateAsync({region: props.country});
  //   plausible('Checkout successful - Fiat Flow');
  //   // const {paymentId} = response;
  //   if (props.isTraxxStemz) {
  //     // router.push(`/traxxstemz/${traxxStemzSlug}?paymentId=${paymentId}`);
  //   } else if (props.isEmilG) {
  //     // router.push(`/emilg/${emilGSlug}?paymentId=${paymentId}`);
  //   } else if (props.isJoshSavage) {
  //     router.push(
  //       `/josh-savage/${joshSavageSlug}?paymentId=${response.transactionId}`,
  //     );
  //   } else {
  //     router.push(`/tracks/${props.slug}?paymentId=${response.transactionId}`);
  //   }
  // };

  const paymentType =
    modalVariant === 'signupin-cash' ? PaymentType.Cash : PaymentType.Crypto;

  const userMintInfo = useMemo(() => {
    if (!walletAddress) return {address: '', maxQuantity: ''};
    return (
      JOSH_WHITELISTED_USERS.find(
        (el) => el.address.toLowerCase() === walletAddress.toLowerCase(),
      ) || {address: '', maxQuantity: ''}
    );
  }, [walletAddress]);

  const proofGeneration = () => {
    if (!walletAddress) return;
    const buf2hex = (buffer: Buffer) => {
      const bufferArray = new Uint8Array(buffer);
      return Array.from(bufferArray)
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('');
    };
    if (props.isJoshSavage && userMintInfo.address) {
      const parsedUserList = JOSH_WHITELISTED_USERS.map((item) =>
        ethers.utils.solidityPack(
          ['address', 'uint256'],
          [item.address, item.maxQuantity],
        ),
      );
      const leaves = parsedUserList.map((x: any) => keccak256(x));
      const tree = new MerkleTree(leaves, keccak256, {sortPairs: true});
      const leaf = keccak256(
        ethers.utils.solidityPack(
          ['address', 'uint256'],
          [userMintInfo.address, userMintInfo.maxQuantity],
        ),
      );

      const proofArray = tree
        .getProof(leaf)
        // eslint-disable-next-line prefer-template
        .map((x: {data: any}) => '0x' + buf2hex(x.data));
      // eslint-disable-next-line consistent-return
      return proofArray;
    }
    if (!props.whiteList?.length || !props.isAllowedUser || !walletAddress)
      // eslint-disable-next-line consistent-return
      return [];
    const tree = new MerkleTree(
      props.whiteList.map((x) => keccak256(x)),
      keccak256,
      {sortPairs: true},
    );
    const leaf = keccak256(walletAddress);

    // eslint-disable-next-line consistent-return
    return tree.getProof(leaf).map((x) => `0x${buf2hex(x.data)}`);
  };

  const contractInfo = {
    walletAddress,
    _listingId: props.listingId,
    _currency: props.currency,
    _price: props.buyoutPrice,
    _quantityToBuy: props.quantity,
    _buyer: walletAddress,
    _proof: proofGeneration(),
    _signatureTimestamp: discountTimeStamp,
    signature: discountSignature,
    price: toNativeCurrencyString(props.buyoutPrice, 'ETH'),
    currency: (network === 'ethereum' ? 'ETH' : 'MATIC') as FormattableCurrency,
  };
  const joshContractInfo = {
    contractId: 'b732dae4-d0bf-48a4-acb1-bae9dced0c95',
    _buyer: walletAddress,
    _quantityToBuy: props.quantity,
  };

  // const joshMintMethod = {
  //   name: 'mint',
  //   args: {
  //     to: walletAddress,
  //     quantity: joshContractInfo._quantityToBuy,
  //     maxQuantity: userMintInfo.maxQuantity,
  //     proof: proofGeneration(),
  //   },
  //   payment: {
  //     value: `0 * $QUANTITY`,
  //     currency: 'MATIC',
  //   },
  // };

  const modalVariantComponent = useMemo(() => {
    const purchaseQuantityProp =
      !Array.isArray(data) && paymentId && data?.purchaseQuantity
        ? Number(data?.purchaseQuantity)
        : props.quantity;
    switch (modalVariant) {
      case 'why-connect-wallet-modal':
        return (
          <WhyConnectWalletModal
            onClose={closeModal}
            onPressButton={() => {
              setTrackNetwork(NETWORK);
              setModalVariant('signupin-cash');
            }}
          />
        );
      case 'cash-or-crypto':
        return (
          <PaymentMethodModal
            onClose={closeModal}
            onPressCash={handleSelectCashPayment}
            onPressCrypto={handleSelectCryptoPayment}
          />
        );
      case 'cash-nft-checkout':
        return (
          <SquareupCheckoutModal
            {...props.cash}
            {...props}
            onModalClose={() => closeModal()}
            contractInfo={props.isJoshSavage ? joshContractInfo : contractInfo}
            isWidgetVisible={isWidgetVisible}
            transactionFee={gasFee}
            total={totalFee}
            isLoading={widgetLoading}
            network={network || ''}
          />
        );
      case 'fiat-paused':
        return <FIATPaymentPaused onClose={closeModal} />;
      case 'cash-payment-details':
        return (
          <CashPaymentDetailsModal
            {...props.cash}
            {...props}
            onSubmit={props.onStartCashCheckout}
            onModalClose={() => closeModal()}
          />
        );
      case 'awaiting-signature-crypto':
        return null;
      case 'awaiting-signature-cash':
        return null;
      case 'checkout':
        return (
          <CompleteCheckoutModal
            {...props.crypto}
            {...props}
            showQuantitySelector={
              props.isMultiCopy ||
              props.isTraxxStemz ||
              props.isEmilG ||
              props.isJoshSavage
            }
            onStartCheckoutPress={handleBuyWithCrypto}
            onModalClose={closeModal}
            currencySymbol={currencySymbol}
            isMember={isMember}
          />
        );
      case 'missing-tokens':
        return (
          // <MissingTokensModal
          //   currencySymbol={currencySymbol ?? 'the right currency'}
          //   onClose={closeModal}
          //   network={network}
          //   walletNativeCurrencyBalance={walletNativeCurrencyBalance}
          //   insufficientMatic={insufficientNativeCurrency}
          //   insufficientTraxx={insufficientTraxx}
          //   total={props.crypto.total}
          //   onOpenWallet={() => {
          //     closeModal();
          //     openWallet();
          //   }}
          // />
          <LiFiCashOrCrypo
            imageSrc={props.imageSrc || ''}
            trackName={props.trackName || ''}
            artist={props.artist || ''}
            quantity={purchaseQuantityProp}
            onModalClose={closeModal}
            handleCashFlow={() => {
              setModalVariant('cash-nft-checkout');
            }}
            handleExchangeFlow={handleWidgetModal}
          />
        );
      case 'cash-processing':
        return (
          <CashProcessingModal
            userEmail={props.userEmail || ''}
            imageSrc={props.imageSrc || ''}
            trackName={props.trackName || ''}
            artist={props.artist || ''}
            quantity={purchaseQuantityProp}
            transactionHash={processingTransactionHash ?? ''}
            onModalClose={closeModal}
            onViewTransactions={onViewTransactions}
          />
        );
      case 'cash-pending':
        return (
          <CashPendingModal
            imageSrc={props.imageSrc || ''}
            trackName={props.trackName || ''}
            artist={props.artist || ''}
            quantity={props.quantity}
            transactionHash={processingTransactionHash ?? ''}
            onModalClose={closeModal}
          />
        );
      case 'cash-failed':
        return (
          <CashFailedModal
            imageSrc={props.imageSrc || ''}
            trackName={props.trackName || ''}
            artist={props.artist || ''}
            quantity={props.quantity}
            transactionHash={processingTransactionHash ?? ''}
            onModalClose={onModalClose}
            onViewTransactions={onViewTransactions}
            onTryAgain={onPressPurchaseButton}
          />
        );
      case 'crypto-processing':
        return (
          <CryptoProcessingModal
            imageSrc={props.imageSrc || ''}
            transactionHash={processingTransactionHash ?? ''}
            onModalClose={onModalClose}
          />
        );
      case 'signupin-crypto':
      case 'signupin-cash':
        return (
          <SignUpInModal
            onClose={closeModal}
            network={NETWORK as Network}
            paymentType={paymentType}
          />
        );
      case 'missing-guest':
        return <GuestUserModal onClose={closeModal} />;
      case 'max-purchase':
        return <MaxPurchaseModal onClose={closeModal} />;
      case 'wrong-network':
        return (
          <WrongNetworkModal
            onClose={closeModal}
            onPressButton={async () => {
              setTrackNetwork(NETWORK || 'zilliqa');
              await handleChangeNetwork();
            }}
            network={network}
          />
        );
      case 'success':
        return (
          <SuccessModal
            onClose={onModalClose}
            imageSrc={props.imageSrc}
            trxHash={processingTransactionHash || ''}
            artist={props.artist}
            trackName={props.trackName}
            quantity={purchaseQuantityProp}
            isTraxxStemz={props.isTraxxStemz}
            isEmilG={props.isEmilG}
            isJoshSavage={props.isJoshSavage}
            tokenId={purchaseTokenIds}
            onPress={onSuccessOkPress}
          />
        );
      case 'dynamic-content':
        return <DynamicContentModal onClose={closeModal} />;
      case 'multidrop':
        return (
          <MultiDropModal
            title="Select network"
            options={[
              {
                id: 'ethereum',
                label: 'Ethereum',
                onPress: () => {
                  router.push('/tracks/low-lives-ethereum');
                },
                icon: <EthereumNetworkIcon size={14} color="textDefault" />,
              },
              {
                id: 'polygon',
                label: 'Polygon',
                onPress: () => {
                  router.push('/tracks/low-lives-matic');
                },
                icon: <PolygonIcon size={14} color="textDefault" />,
              },
              {
                id: 'traxx',
                label: 'Traxx',
                onPress: () => {
                  router.push('/tracks/low-lives-traxx');
                },
                icon: <TokenTraxxIcon size={14} color="textDefault" />,
              },
              {
                id: 'zilliqa',
                label: 'Zilliqa',
                onPress: () => {
                  router.push('/multidrop-form');
                },
                icon: <ZilliqaIcon size={14} color="textDefault" />,
              },
              // {
              //   id: 'bitcoin',
              //   label: 'Bitcoin',
              //   onPress: () => {
              //     router.push('/multidrop-form?network=bitcoin');
              //   },
              //   icon: <BitcoinIcon size={14} color="textDefault" />,
              // },
            ]}
            onClose={closeModal}
          />
        );
      case 'token-owner':
        return <TokenOwnerModal onClose={closeModal} />;
      case 'pick-nft':
        return (
          <DynamicConfirmationModal
            title={PICK_NFT_TITLE}
            options={[
              {
                id: 'standard',
                label: (
                  <>
                    Sonic Starshifters{' '}
                    <Typography textStyle="xs" color="textDefault">
                      (includes e-book version and out of this world utility)
                    </Typography>
                  </>
                ),
                onPress: () => {
                  setSessionItem('PICKED_NFT', 'standard');
                  setModalVariant(null);
                  if (
                    props.whiteList?.length &&
                    walletAddress &&
                    checkAddressExistsInWhiteList(
                      props.whiteList,
                      walletAddress,
                    )
                  ) {
                    router.push(`/tracks/${SARA_DROP.standardWhiteList}`);
                  } else {
                    router.push(`/tracks/${SARA_DROP.standardPublic}`);
                  }
                },
              },
              {
                id: 'premium',
                label: (
                  <>
                    Resonant Savants{' '}
                    <Typography textStyle="xs" color="textDefault">
                      (includes extra access and utility)
                    </Typography>
                  </>
                ),
                onPress: () => {
                  setSessionItem('PICKED_NFT', 'premium');
                  setModalVariant(null);
                  if (
                    props.whiteList?.length &&
                    walletAddress &&
                    checkAddressExistsInWhiteList(
                      props.whiteList,
                      walletAddress,
                    )
                  ) {
                    router.push(`/tracks/${SARA_DROP.premiumWhiteList}`);
                  } else {
                    router.push(`/tracks/${SARA_DROP.premiumPublic}`);
                  }
                },
              },
            ]}
          />
        );
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    modalVariant,
    closeModal,
    handleSelectCashPayment,
    handleSelectCryptoPayment,
    props,
    handleBuyWithCrypto,
    currencySymbol,
    network,
    walletNativeCurrencyBalance,
    insufficientNativeCurrency,
    processingTransactionHash,
    onModalClose,
    onViewTransactions,
    onPressPurchaseButton,
    setModalVariant,
    openWallet,
    handleChangeNetwork,
    router,
  ]);

  return {
    modalVariantComponent,
    onPressPurchase: onPressPurchaseButton,
  };
};

